import * as THREE from 'three'
import type { CloneWithSharedGeometryOptions } from '../../../types'
import { CadObjectPersistenceMode, readCadObjectPersistenceMode } from '../../document'
import type { GLTF } from 'three/examples/jsm/loaders/GLTFLoader.js'
import type { FileInfo } from './file-info'
import {
  buildParserNodeBodyEntriesMap,
  mergeBodyEntriesById,
} from './body-entries'
import { collectGlbHierarchy, extractPartName, type ImportedBodyEntry } from './glb'
import { applyOriginalNodeNamesToScene } from './original-node-name'
import { IMPORTED_MODEL_RUNTIME_CACHE_REVISION } from './runtime-cache-revision'
import {
  activateImportedModelTopologyHosts,
  disposeImportedModelTopologyHosts,
  isImportedModelTopologyHost,
} from '../topology/imported-model-topology-runtime'
import { ImportedModelObjectMetadataService } from '../metadata'
import {
  isFeatureFace,
  isFeatureLine,
  isFeaturePoint,
  //------ 模型显示模式 开始------
  FeatureTopologyDisplayEdgesMesh,
  //------ 模型显示模式 结束------
  FeatureResultMesh,
  isFeatureResultMesh,
} from '../../mesh/feature'
import { asRecord } from './utils'
import { inheritStrippedRootUserData } from './stripped-root-user-data'
import { applyGltfBrepBindingPlansToScene } from './imported-brep-binding-plan'
import { resolveImportedModelUnitContextFromGltf } from './imported-model-unit-context'
import { buildImportedTopologySource } from './topology-source/buildImportedTopologySource'
import type {
  CachedPartResult,
  ImportedModelUnitContext,
  LoaderMetadata,
  PartAsset,
  PartAssetLoadResult,
} from './types'

function cloneMetadata(value: unknown): LoaderMetadata | undefined {
  const record = asRecord(value)
  if (!record) {
    return undefined
  }
  return { ...record }
}

//------ 装配体引用复制 FeatureFace 克隆修复 开始------
/**
 * 判断对象是否为导入拓扑临时代理。
 *
 * 设计说明：
 * 1. FeatureFace / FeatureLine / FeaturePoint 是交互时按需挂载在结果体下的拓扑代理；
 * 2. FeatureTopologyDisplayEdgesMesh 是显示模式按拓扑快照生成的运行时合并边；
 * 3. imported-model 实例边线与正式表面共享 instanceMatrix，只能由实例 Unit 重建；
 * 4. imported topology pick host 是二阶段拾取宿主，不属于正式可见模型树；
 * 5. 它们的构造函数需要显式几何或位置参数，不能走 Three.js 默认 `clone(false)`；
 * 6. 引用复制只需要复制组件 occurrence 和权威结果体，代理对象可由拓扑仓库按需重建。
 *
 * @param object 待判断对象。
 * @returns 是否应在共享几何克隆时跳过。
 */
function shouldSkipTransientTopologyProxyClone(object: THREE.Object3D): boolean {
  return isFeatureFace(object)
    || isFeatureLine(object)
    || isFeaturePoint(object)
    //------ 模型显示模式 开始------
    || object instanceof FeatureTopologyDisplayEdgesMesh
    || object.userData.isImportedModelInstancedTopologyDisplayEdges === true
    //------ 模型显示模式 结束------
    || isImportedModelTopologyHost(object)
}
//------ 装配体引用复制 FeatureFace 克隆修复 结束------

//------ 装配体引用复制拓扑选择修复 开始------
/**
 * 共享几何克隆映射结果。
 */
export interface CloneWithSharedGeometryRuntimeMap<T extends THREE.Object3D> {
  /** 克隆后的对象根。 */
  readonly object: T
  /** 来源对象到克隆对象的运行时映射。 */
  readonly clonedObjectBySourceObject: WeakMap<THREE.Object3D, THREE.Object3D>
  /** 来源对象 UUID 到克隆对象 UUID 的映射。 */
  readonly clonedObjectIdBySourceObjectId: Map<string, string>
}

/**
 * 记录共享几何克隆映射。
 */
type CloneWithSharedGeometryRecorder = (source: THREE.Object3D, cloned: THREE.Object3D) => void
//------ 装配体引用复制拓扑选择修复 结束------

/**
 * 递归克隆 Object3D 树，但共享 Mesh/Line 的 geometry 和 material 引用（THREE.js clone 默认行为），
 * 同时将 userData 改为浅拷贝而非 JSON.parse/stringify 深拷贝。
 *
 * 对于大型模型，每个 Mesh 节点的 userData 可能含有数万条 faceTriangleRanges 记录，
 * 深拷贝会产生大量临时字符串 + 内存分配，改为浅拷贝后这些只读数组由模板和实例共享。
 * geometry 数据（TypedArray）在 THREE.js 中本身已共享，无需重复复制。
 */
function cloneWithSharedGeometryInternal<T extends THREE.Object3D>(
  source: T,
  recorder?: CloneWithSharedGeometryRecorder,
  options?: CloneWithSharedGeometryOptions,
): T {
  if (isFeatureResultMesh(source)) {
    const positionAttribute = source.geometry.getAttribute('position')
    const normalAttribute = source.geometry.getAttribute('normal')
    if (!(positionAttribute instanceof THREE.BufferAttribute) || !(normalAttribute instanceof THREE.BufferAttribute)) {
      throw new Error(`导入拓扑宿主缺少 position/normal，无法克隆: ${source.objectId}`)
    }

    const indexAttribute = source.geometry.getIndex()
    const clonedHost = new FeatureResultMesh({
      meshData: {
        id: source.objectId,
        positions: positionAttribute.array as Float32Array,
        normals: normalAttribute.array as Float32Array,
        indices: indexAttribute?.array as Uint32Array | undefined,
        metadata: source.getFeatureMetadata(),
        faceTriangleRanges: source.getFaceTriangleRanges(),
      },
      geometry: source.geometry,
      material: source.material,
    })

    clonedHost.name = source.name
    clonedHost.position.copy(source.position)
    clonedHost.quaternion.copy(source.quaternion)
    clonedHost.scale.copy(source.scale)
    clonedHost.matrix.copy(source.matrix)
    clonedHost.matrixWorld.copy(source.matrixWorld)
    clonedHost.matrixAutoUpdate = source.matrixAutoUpdate
    clonedHost.matrixWorldNeedsUpdate = source.matrixWorldNeedsUpdate
    clonedHost.visible = source.visible
    clonedHost.renderOrder = source.renderOrder
    clonedHost.frustumCulled = source.frustumCulled
    clonedHost.castShadow = source.castShadow
    clonedHost.receiveShadow = source.receiveShadow
    clonedHost.layers.mask = source.layers.mask
    clonedHost.userData = { ...source.userData }
    ImportedModelObjectMetadataService.copyObjectMetadata(source, clonedHost)
    clonedHost.syncTopologyVisuals(source.getTopologySnapshot())
    clonedHost.setTopologySelectionMode('none')
    recorder?.(source, clonedHost)

    for (const child of options?.listSourceChildren?.(source) ?? source.children) {
      //------ 装配体引用复制 FeatureFace 克隆修复 开始------
      if (shouldSkipTransientTopologyProxyClone(child)
        || (options && readCadObjectPersistenceMode(child) === CadObjectPersistenceMode.RuntimeDerived)) {
        continue
      }
      //------ 装配体引用复制 FeatureFace 克隆修复 结束------
      const clonedChild = cloneWithSharedGeometryInternal(child, recorder, options)
      // 脱离状态属于源实例投影，副本重新进入正常父子矩阵传播，不能继承冻结状态。
      if (child.parent !== source) clonedChild.matrixWorldAutoUpdate = true
      clonedHost.add(clonedChild)
    }

    return clonedHost as unknown as T
  }

  const originalUserData = source.userData
  // 临时置空，让 clone(false) 内部的 JSON.parse/stringify 处理空对象（几乎零开销）
  source.userData = {}
  let cloned: T
  try {
    cloned = source.clone(false) as T
  } finally {
    // 即使特殊对象克隆失败，也不能破坏源对象元数据。
    source.userData = originalUserData
  }
  // 浅拷贝：geometry/material 只读，faceTriangleRanges 等大数组由模板与实例共享
  cloned.userData = { ...originalUserData }
  ImportedModelObjectMetadataService.copyObjectMetadata(source, cloned)
  recorder?.(source, cloned)

  for (const child of options?.listSourceChildren?.(source) ?? source.children) {
    //------ 装配体引用复制 FeatureFace 克隆修复 开始------
    if (shouldSkipTransientTopologyProxyClone(child)
      || (options && readCadObjectPersistenceMode(child) === CadObjectPersistenceMode.RuntimeDerived)) {
      continue
    }
    //------ 装配体引用复制 FeatureFace 克隆修复 结束------
    const clonedChild = cloneWithSharedGeometryInternal(child, recorder, options)
    if (child.parent !== source) clonedChild.matrixWorldAutoUpdate = true
    cloned.add(clonedChild)
  }
  return cloned
}

export function cloneWithSharedGeometry<T extends THREE.Object3D>(source: T): T {
  return cloneWithSharedGeometryInternal(source)
}

//------ 装配体引用复制拓扑选择修复 开始------
/**
 * 递归克隆 Object3D 树并返回来源对象到克隆对象的映射。
 *
 * 设计说明：
 * 装配体引用复制需要在提交后复制 imported topology runtime。由于克隆过程会跳过
 * FeatureFace / FeatureLine / FeaturePoint / pick host 等临时代理，不能再用 children index
 * 建立映射，必须在真实克隆发生时同步记录映射关系。
 *
 * @param source 来源对象。
 * @param options 实例化组件的逻辑层级读取端口。
 * @returns 克隆对象与运行时映射。
 */
export function cloneWithSharedGeometryRuntimeMap<T extends THREE.Object3D>(
  source: T,
  options?: CloneWithSharedGeometryOptions,
): CloneWithSharedGeometryRuntimeMap<T> {
  const clonedObjectBySourceObject = new WeakMap<THREE.Object3D, THREE.Object3D>()
  const clonedObjectIdBySourceObjectId = new Map<string, string>()
  const object = cloneWithSharedGeometryInternal(source, (sourceObject, clonedObject) => {
    clonedObjectBySourceObject.set(sourceObject, clonedObject)
    clonedObjectIdBySourceObjectId.set(sourceObject.uuid, clonedObject.uuid)
  }, options)
  // 单 Mesh 本身也可能是已脱离的 occurrence 根，副本不得继承源投影的世界矩阵冻结状态。
  if (options?.listSourceChildren) object.matrixWorldAutoUpdate = true

  return {
    object,
    clonedObjectBySourceObject,
    clonedObjectIdBySourceObjectId,
  }
}
//------ 装配体引用复制拓扑选择修复 结束------

export function hasLocalTransform(object: THREE.Object3D, tolerance = 1e-6): boolean {
  const isZeroVector = (value: THREE.Vector3): boolean =>
    Math.abs(value.x) <= tolerance && Math.abs(value.y) <= tolerance && Math.abs(value.z) <= tolerance
  const isIdentityQuaternion = (value: THREE.Quaternion): boolean =>
    Math.abs(value.x) <= tolerance
    && Math.abs(value.y) <= tolerance
    && Math.abs(value.z) <= tolerance
    && Math.abs(value.w - 1) <= tolerance
  const isUnitScale = (value: THREE.Vector3): boolean =>
    Math.abs(value.x - 1) <= tolerance
    && Math.abs(value.y - 1) <= tolerance
    && Math.abs(value.z - 1) <= tolerance

  return !isZeroVector(object.position) || !isIdentityQuaternion(object.quaternion) || !isUnitScale(object.scale)
}

export function resetLocalTransform(object: THREE.Object3D): void {
  object.position.set(0, 0, 0)
  object.quaternion.identity()
  object.scale.set(1, 1, 1)
  object.updateMatrix()
  object.updateMatrixWorld(true)
}

function resolveCanonicalPartRoot(root: THREE.Object3D): {
  canonicalRoot: THREE.Object3D
  strippedNodeCount: number
  strippedWrappers: THREE.Object3D[]
} {
  let current = root
  let strippedNodeCount = 0
  const strippedWrappers: THREE.Object3D[] = []

  while (
    !(current instanceof THREE.Mesh)
    && !(current instanceof THREE.Line)
    && !(current instanceof THREE.Points)
    && current.children.length === 1
  ) {
    strippedWrappers.push(current)
    current = current.children[0]
    strippedNodeCount += 1
  }

  return {
    canonicalRoot: current,
    strippedNodeCount,
    strippedWrappers,
  }
}

function createNormalizedPartTemplate(
  sourceRoot: THREE.Group,
  partName: string,
): { renderTemplate: THREE.Group; normalizationWarnings: string[] } {
  const { canonicalRoot, strippedNodeCount, strippedWrappers } = resolveCanonicalPartRoot(sourceRoot)
  const normalizationWarnings: string[] = []
  const template = new THREE.Group()
  template.name = partName
  const sourceUserData = asRecord(sourceRoot.userData) ?? {}
  const { importedPartBodyEntries: _ignoredBodyEntries, bodyCount: _ignoredBodyCount, ...templateUserData } = sourceUserData
  void _ignoredBodyEntries
  void _ignoredBodyCount
  template.userData = {
    ...templateUserData,
  }
  ImportedModelObjectMetadataService.writeMetadata(template, {
    runtimeWrapper: true,
  })

  inheritStrippedRootUserData(canonicalRoot, strippedWrappers)
  const normalizedRoot = cloneWithSharedGeometry(canonicalRoot)
  const rootHadTransform = hasLocalTransform(normalizedRoot)
  if (rootHadTransform) {
    normalizationWarnings.push(
      `零件模板根节点存在非单位变换，已在前端归一化: ${partName} (${canonicalRoot.name || canonicalRoot.type})`,
    )
  }
  if (strippedNodeCount > 0) {
    normalizationWarnings.push(
      `零件模板已剥离 ${strippedNodeCount} 层单子节点包装层: ${partName}`,
    )
  }

  resetLocalTransform(normalizedRoot)
  template.add(normalizedRoot)
  return {
    renderTemplate: template,
    normalizationWarnings,
  }
}

function resolveCachedPartHierarchyRoot(root: THREE.Group): THREE.Object3D {
  const isRuntimeWrapper = ImportedModelObjectMetadataService.peekMetadata(root)?.runtimeWrapper === true
  if (isRuntimeWrapper && root.children.length === 1) {
    return root.children[0]
  }
  return root
}

export function resolveReferencedPartHierarchyRoot(root: THREE.Object3D): THREE.Object3D {
  const isRuntimeWrapper = ImportedModelObjectMetadataService.peekMetadata(root)?.runtimeWrapper === true
  if (isRuntimeWrapper && root.children.length === 1) {
    return root.children[0]
  }
  return root
}

function applyNodeBodyEntriesToScene(
  root: THREE.Object3D,
  associations: unknown,
  nodeBodyEntries: Map<number, ImportedBodyEntry[]>,
  nodePrimitiveEntityMap: Map<number, Map<number, ImportedBodyEntry>>,
): ImportedBodyEntry[] {
  const associationMap = associations instanceof Map
    ? associations as Map<unknown, unknown>
    : null
  const aggregatedEntries: ImportedBodyEntry[][] = []

  root.traverse((object) => {
    delete object.userData.importedPartBodyEntries
    delete object.userData.bodyCount
    delete object.userData.primitiveEntityEntries

    const associationRecord = asRecord(associationMap?.get(object))
    const nodeIndex = typeof associationRecord?.nodes === 'number' ? associationRecord.nodes : null
    if (nodeIndex === null) {
      return
    }

    const bodyEntries = nodeBodyEntries.get(nodeIndex)
    if (!bodyEntries || bodyEntries.length === 0) {
      return
    }

    object.userData.importedPartBodyEntries = bodyEntries
    object.userData.bodyCount = bodyEntries.length
    aggregatedEntries.push(bodyEntries)

    // 将 primitiveIndex → entity 映射写入 userData，供后续构建 primitiveCatalog 和选择系统使用
    const primitiveEntityMap = nodePrimitiveEntityMap.get(nodeIndex)
    if (primitiveEntityMap && primitiveEntityMap.size > 0) {
      // 将 Map 转为数组格式存入 userData，避免非普通对象被响应式系统捕获
      const primitiveEntityEntries: Array<{ primitiveIndex: number; bodyId: string; label: string }> = []
      primitiveEntityMap.forEach((entry, primitiveIndex) => {
        primitiveEntityEntries.push({ primitiveIndex, bodyId: entry.bodyId, label: entry.label })
      })
      object.userData.primitiveEntityEntries = primitiveEntityEntries
    }
  })

  return mergeBodyEntriesById(...aggregatedEntries)
}

/**
 * 将 GLB 中 meshes[].step.entities 解析的实体信息写入 GLTF scene 各节点 userData，
 * 并返回汇总后的实体列表。
 * 用于 embedded 装配文件在存储 sceneTemplate 前写入实体数据，
 * 使后续 collectGlbHierarchy 能正确读取 importedPartBodyEntries。
 */
export function applyGltfBodyEntriesToScene(gltf: GLTF): ImportedBodyEntry[] {
  applyOriginalNodeNamesToScene(gltf)
  const parserJson = (gltf.parser as unknown as { json?: unknown }).json
  const associations = (gltf.parser as unknown as { associations?: unknown }).associations
  const { nodeBodyEntries, nodePrimitiveEntityMap } = buildParserNodeBodyEntriesMap(parserJson)
  return applyNodeBodyEntriesToScene(gltf.scene, associations, nodeBodyEntries, nodePrimitiveEntityMap)
}

export function parsePartAssetGltf(
  gltf: GLTF,
  file: FileInfo,
  version: string,
): PartAssetLoadResult {
  applyOriginalNodeNamesToScene(gltf)
  applyGltfBrepBindingPlansToScene(gltf)
  const sceneRecord = asRecord(gltf.scene.userData) ?? {}
  const parserJson = (gltf.parser as unknown as { json?: unknown }).json
  const unitContext = resolveImportedModelUnitContextFromGltf(gltf)
  const { nodeBodyEntries, nodePrimitiveEntityMap } = buildParserNodeBodyEntriesMap(parserJson)
  const sceneBodyEntries = applyNodeBodyEntriesToScene(
    gltf.scene,
    (gltf.parser as unknown as { associations?: unknown }).associations,
    nodeBodyEntries,
    nodePrimitiveEntityMap,
  )
  const partName = extractPartName(file.fileId)
  const {
    renderTemplate,
    normalizationWarnings,
  } = createNormalizedPartTemplate(gltf.scene, partName)
  const warnings: string[] = [...normalizationWarnings]
  const topologySource = buildImportedTopologySource(renderTemplate, {
    step: cloneMetadata(sceneRecord.step ?? sceneRecord.stepMetadata ?? sceneRecord.assetStep),
    pmi: cloneMetadata(sceneRecord.pmi),
    unitContext,
  })
  const {
    primitiveCatalog,
    meshCatalog,
    brepBindingsByMeshId,
    bodyBrepBindings,
    bodyBrepBindingIdByPrimitiveId,
    visibleBoundsByBodyBindingId,
    bodyEntries: topologyBodyEntries,
    bodyPrimitiveIndicesById,
    unitContext: topologyUnitContext,
  } = topologySource
  const meshCount = Object.keys(meshCatalog).length

  if (meshCount === 0) {
    warnings.push(`零件 ${file.fileId} 未解析出任何 Mesh`)
  }

  delete renderTemplate.userData.importedPartBodyEntries
  delete renderTemplate.userData.bodyCount
  const asset: PartAsset = {
    fileUuid: file.uuid,
    fileId: file.fileId,
    version,
    cacheRevision: IMPORTED_MODEL_RUNTIME_CACHE_REVISION,
    partName,
    semanticKind: 'part',
    renderTemplate,
    bodyEntries: topologyBodyEntries.length > 0 ? topologyBodyEntries : sceneBodyEntries,
    bodyPrimitiveIndicesById,
    primitiveCatalog,
    meshCatalog,
    brepBindingsByMeshId,
    bodyBrepBindings,
    bodyBrepBindingIdByPrimitiveId,
    visibleBoundsByBodyBindingId,
    unitContext: topologyUnitContext,
    step: cloneMetadata(sceneRecord.step ?? sceneRecord.stepMetadata ?? sceneRecord.assetStep),
    pmi: cloneMetadata(sceneRecord.pmi),
  }

  return {
    asset,
    sourceGltf: gltf,
    warnings,
  }
}

/**
 * 从装配 GLB 内的 Object3D 子树就地解析为标准 PartAsset。
 * 用于混合装配中无 external_file_uuid 但自带几何的节点，
 * 使其与外部引用零件走同一条组装路径。
 *
 * @param source 源 GLB scene 中该节点对应的 Object3D（Mesh / Group with Mesh children）
 * @param nodeId manifest nodeId，用作虚拟标识
 * @param partName 零件显示名
 * @param assemblyFileUuid 装配文件 UUID，用于构建虚拟 fileUuid
 * @param version 版本号
 */
export function parseInlinePartAsset(
  source: THREE.Object3D,
  nodeId: string,
  partName: string,
  assemblyFileUuid: string,
  version: string,
  unitContext?: ImportedModelUnitContext,
): PartAsset {
  const cloned = cloneWithSharedGeometry(source)
  const wrapperGroup = new THREE.Group()
  wrapperGroup.name = partName
  ImportedModelObjectMetadataService.writeMetadata(wrapperGroup, {
    runtimeWrapper: true,
  })

  // inline part 需要保留子树内部全部局部变换，只剥离当前实例节点自身的变换。
  resetLocalTransform(cloned)
  wrapperGroup.add(cloned)
  const renderTemplate = wrapperGroup
  const {
    bodyEntries: topologyBodyEntries,
    bodyPrimitiveIndicesById,
    primitiveCatalog,
    meshCatalog,
    brepBindingsByMeshId,
    bodyBrepBindings,
    bodyBrepBindingIdByPrimitiveId,
    visibleBoundsByBodyBindingId,
    unitContext: topologyUnitContext,
  } = buildImportedTopologySource(renderTemplate, {
    unitContext,
  })

  // 虚拟 fileUuid：用 inline: 前缀 + nodeId 标识，与外部零件区分
  const inlineFileUuid = `inline:${assemblyFileUuid}:${nodeId}`

  delete renderTemplate.userData.importedPartBodyEntries
  delete renderTemplate.userData.bodyCount

  return {
    fileUuid: inlineFileUuid,
    fileId: `inline-part:${nodeId}`,
    version,
    cacheRevision: IMPORTED_MODEL_RUNTIME_CACHE_REVISION,
    partName,
    semanticKind: 'part',
    renderTemplate,
    bodyEntries: topologyBodyEntries,
    bodyPrimitiveIndicesById,
    primitiveCatalog,
    meshCatalog,
    brepBindingsByMeshId,
    bodyBrepBindings,
    bodyBrepBindingIdByPrimitiveId,
    visibleBoundsByBodyBindingId,
    unitContext: topologyUnitContext,
  }
}

function cloneCachedPartObject(asset: PartAsset, file: FileInfo): THREE.Group {
  const object = cloneWithSharedGeometry(asset.renderTemplate)
  object.name = extractPartName(file.fileId)
  ImportedModelObjectMetadataService.writeMetadata(object, {
    fileInfo: {
      fileId: file.fileId,
      uuid: file.uuid,
      partName: object.name,
    },
  })
  return object
}

export function createCachedPartResult(asset: PartAsset, file: FileInfo): CachedPartResult {
  const object = cloneCachedPartObject(asset, file)
  const hierarchyRoot = resolveCachedPartHierarchyRoot(object)
  const { hierarchy, rows } = collectGlbHierarchy(hierarchyRoot, file)
  activateImportedModelTopologyHosts(object, asset)

  return {
    kind: 'cached-part',
    fileInfo: file,
    partName: object.name,
    object,
    hierarchy,
    hierarchyRows: rows,
    sourceAssetKey: `${asset.fileUuid}:${asset.version}`,
    asset,
  }
}

export function disposeCachedPartResult(result: CachedPartResult): void {
  disposeImportedModelTopologyHosts(result.object)
  result.object.removeFromParent()
  result.object.clear()
}
